import React, { useCallback, useEffect, useState } from "react";
import Wrapper from "../../../components/Wrapper";
import { labApi, hardwareApi } from "../../../services/labApi";
import { formatHardwareLabel } from "./hardwareUtils";

export default function WorkLogsPage() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [logs, setLogs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [reagents, setReagents] = useState([]);
  const [hardwareList, setHardwareList] = useState([]);

  const [formData, setFormData] = useState({
    worker_id: "",
    reagent_id: "",
    hardware_id: "",
    quantity_completed: "",
    start_time: "",
    end_time: "",
    notes: "",
  });

  const load = useCallback(async () => {
    const [logRows, workerRows, reagentRows, hardwareRows] = await Promise.all([
      labApi.getWorkLogsByDate(selectedDate),
      labApi.getActiveCrew(),
      labApi.getReagents(),
      hardwareApi.getHardware(),
    ]);

    setLogs(logRows);
    setWorkers(workerRows);
    setReagents(reagentRows);
    setHardwareList(hardwareRows);
  }, [selectedDate]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const createLog = async (e) => {
    e.preventDefault();
    await labApi.createWorkLog({
      worker_id: formData.worker_id,
      reagent_id: formData.reagent_id || null,
      hardware_id: formData.hardware_id || null,
      quantity_completed: Number(formData.quantity_completed || 0),
      start_time: formData.start_time,
      end_time: formData.end_time || null,
      notes: formData.notes || null,
      date: selectedDate,
    });
    // Reset form
    setFormData({ 
      worker_id: "", 
      reagent_id: "", 
      hardware_id: "", 
      quantity_completed: "", 
      start_time: "", 
      end_time: "", 
      notes: "" 
    });
    await load();
  };

  const selectedItemValue = formData.reagent_id 
    ? `reagent_${formData.reagent_id}` 
    : formData.hardware_id 
      ? `hardware_${formData.hardware_id}` 
      : "";

  const handleItemChange = (e) => {
    const val = e.target.value;
    if (val.startsWith("reagent_")) {
      setFormData({ ...formData, reagent_id: val.replace("reagent_", ""), hardware_id: "" });
    } else if (val.startsWith("hardware_")) {
      setFormData({ ...formData, hardware_id: val.replace("hardware_", ""), reagent_id: "" });
    } else {
      setFormData({ ...formData, hardware_id: "", reagent_id: "" });
    }
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <h2 className="m-0 text-2xl font-bold text-slate-800">Work Logs</h2>
        <input 
          type="date" 
          value={selectedDate} 
          onChange={(e) => setSelectedDate(e.target.value)} 
          className="px-3 py-2 border rounded-lg" 
        />

        <form onSubmit={createLog} className="grid md:grid-cols-3 gap-3 bg-white border rounded-xl p-4 shadow-sm">
          <select 
            value={formData.worker_id} 
            onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })} 
            className="px-3 py-2 border rounded-lg" 
            required
          >
            <option value="">Worker</option>
            {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>

          {/* Combined Dropdown for Reagents & Hardware */}
          <select 
            value={selectedItemValue} 
            onChange={handleItemChange} 
            className="px-3 py-2 border rounded-lg" 
            required
          >
            <option value="">Select Item (Reagent / Hardware)</option>
            <optgroup label="Reagents">
              {reagents.map((r) => <option key={`r-${r.id}`} value={`reagent_${r.id}`}>{r.name}</option>)}
            </optgroup>
            <optgroup label="Hardware">
              {hardwareList.map((h) => (
                <option key={`h-${h.id}`} value={`hardware_${h.id}`}>{formatHardwareLabel(h)}</option>
              ))}
            </optgroup>
          </select>

          <input 
            value={formData.quantity_completed} 
            onChange={(e) => setFormData({ ...formData, quantity_completed: e.target.value })} 
            type="number" 
            min="1"
            placeholder="Quantity completed" 
            className="px-3 py-2 border rounded-lg" 
            required 
          />
          
          <input 
            value={formData.start_time} 
            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} 
            type="time" 
            className="px-3 py-2 border rounded-lg text-slate-600" 
            required 
          />
          <input 
            value={formData.end_time} 
            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} 
            type="time" 
            className="px-3 py-2 border rounded-lg text-slate-600" 
          />
          <input 
            value={formData.notes} 
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
            placeholder="Notes" 
            className="px-3 py-2 border rounded-lg" 
          />
          
          <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 transition-colors text-white font-medium rounded-lg md:col-span-3">
            Log Work
          </button>
        </form>

        <div className="space-y-3">
          {logs.length === 0 ? (
            <p className="text-slate-500 italic py-4">No work logged for this date.</p>
          ) : null}

          {logs.map((l) => (
            <div key={l.id} className="bg-white border rounded-xl p-4 shadow-sm">
              <p className="m-0 font-semibold text-slate-800">
                {l.worker_name} - {l.item_name || l.reagent_name || l.hardware_name}
              </p>
              <p className="m-0 text-sm text-slate-500 mt-0.5">
                Qty: <span className="font-medium text-slate-700">{l.quantity_produced || l.quantity_completed}</span> | {l.start_time} - {l.end_time || "ongoing"}
              </p>
              {l.notes ? <p className="m-0 text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded border border-slate-100">{l.notes}</p> : null}
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
}